/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import { get, put, del, postForm } from './client.js'
// ─── Config ───────────────────────────────────────────────────────────────────

export type RestaurantConfig = {
  id: string
  tenant_id: string
  name: string
  about: string | null
  logo_r2_key: string | null
  primary_color: string
  secondary_color: string
  background_color: string
  header_color: string
  button_active_color: string
  logo_bg_color: string | null
  /** Template do cardápio do cliente (0084): 'list' clássico | 'grid' grade 2× */
  menu_template: 'list' | 'grid' | 'feed_1'
  /** Fluxo dos pedidos da mesa (0085): 'full' completo | 'waiter' modo garçom */
  order_flow: 'full' | 'waiter'
  primary_locale: string
  target_locales: string[]
  /** #419/#413: moeda do PAÍS do tenant (API deriva de tenants.country_code) */
  currency?: string | null
  /** #428: país do tenant (ISO 3166-1 alpha-2) — compõe o locale de formatação */
  country_code?: string | null
  table_count: number
  is_open: boolean
  /** Modo de abertura (#332, 0080): 'auto' segue interruptor + horário */
  open_mode: 'auto' | 'open' | 'closed'
  allow_takeaway: boolean
  allow_delivery: boolean
  tip_options: number[]
  tip_mode: 'optional' | 'suggested' | 'none'
  payment_methods: string[]
  /** Fuso IANA do restaurante (ex.: Europe/Lisbon) */
  timezone: string
  /** #129 (0099): pedir NIF/CPF no checkout — 'off' | 'optional' | 'required' */
  fiscal_id_mode: 'off' | 'optional' | 'required'
  /** #133 (0100): SLA de espera do KDS em minutos (default 15) */
  sla_minutes: number
  /** limite do garçom p/ ajuste de preço (0056); null = sem limite */
  waiter_adjust_limit_type?: 'percent' | 'amount' | null
  waiter_adjust_limit_value?: number | null
  created_at: number
  updated_at: number
}

export type OperatingHour = {
  id: string
  tenant_id: string
  day_of_week: number
  open_time: string | null
  close_time: string | null
}

export const config = {
  get: () => get<RestaurantConfig>('/admin/config'),
  /**
   * Fuso do restaurante para QUALQUER papel (garçom/cozinha não leem
   * /admin/config) — vem do payload público /menu/restaurant (#248).
   */
  timezone: () => get<{ timezone?: string }>('/menu/restaurant').then((r) => r.timezone ?? null),
  /** #419/#413/#428: moeda e país do tenant via cardápio público — cobre
   *  garçons sem papel de gestor (que não leem /admin/config). */
  countryCurrency: () =>
    get<{ currency?: string; country_code?: string }>('/menu/restaurant').then((r) => ({
      currency: r.currency ?? null,
      country: r.country_code ?? null,
    })),
  /** Fluxo dos pedidos (0085) via menu público — garçom sem acesso a /admin/config */
  orderFlow: () =>
    get<{ order_flow?: 'full' | 'waiter' }>('/menu/restaurant').then((r) => r.order_flow ?? 'full'),
  /** SLA do KDS (0100/#133) via menu público — cozinha não acessa /admin/config */
  slaMinutes: () =>
    get<{ sla_minutes?: number }>('/menu/restaurant').then((r) => r.sla_minutes ?? 15),
  update: (
    body: Partial<
      Omit<
        RestaurantConfig,
        'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'logo_r2_key' | 'table_count'
      >
    >,
  ) => put<RestaurantConfig>('/admin/config', body),
  uploadLogo: (file: File, bgColor?: string | null) => {
    const form = new FormData()
    form.append('file', file)
    if (bgColor) form.append('bg_color', bgColor)
    return postForm<{ logo_r2_key: string; logo_bg_color: string | null; url: string }>(
      '/admin/config/logo',
      form,
    )
  },
  deleteLogo: () => del<{ message: string }>('/admin/config/logo'),
  getHours: () => get<OperatingHour[]>('/admin/config/hours'),
  setHours: (
    hours: { day_of_week: number; open_time: string | null; close_time: string | null }[],
  ) => put<OperatingHour[]>('/admin/config/hours', { hours }),
}
